/**
 * /database Health card — retention & size (Data Design PRD §7.3).
 *
 * Imported by:
 *   - excellent/app/api/database/retention/health/route.ts   (server)
 *   - packages/ui/src/blocks/database/retention-health-card   (client)
 *
 * The zod shapes mirror RetentionHealth / RetentionPreview in
 * packages/core/src/retention.ts. Keep them in lockstep.
 */

import { z } from "zod";
import type { Operation } from "../../types";

export const retentionPolicySchema = z.object({
  eventPayloadDays: z.number().optional(),
  rawJsonKeepVersions: z.number().optional(),
});
export type RetentionPolicyDto = z.infer<typeof retentionPolicySchema>;

export const categoryPreviewSchema = z.object({
  category: z.string(),
  rows: z.number(),
  bytes: z.number(),
});

export const retentionPreviewSchema = z.object({
  workspaceId: z.number(),
  policy: retentionPolicySchema,
  hasPolicy: z.boolean(),
  categories: z.array(categoryPreviewSchema),
  eventRows: z.number(),
  eventBytes: z.number(),
  rawJsonRows: z.number(),
  rawJsonBytes: z.number(),
  totalBytes: z.number(),
  playExpiredRows: z.number(),
  playExpiredBytes: z.number(),
});
export type RetentionPreviewDto = z.infer<typeof retentionPreviewSchema>;

export const tableSizeSchema = z.object({
  name: z.string(),
  rows: z.number(),
});

export const growthPointSchema = z.object({
  day: z.string(),
  events: z.number(),
});

export const retentionHealthSchema = z.object({
  workspaceId: z.number(),
  pageCount: z.number(),
  pageSize: z.number(),
  logicalBytes: z.number(),
  fileBytes: z.number(),
  topTables: z.array(tableSizeSchema),
  dbstatAvailable: z.boolean(),
  growth: z.array(growthPointSchema),
  preview: retentionPreviewSchema,
});
export type RetentionHealthDto = z.infer<typeof retentionHealthSchema>;

export const retentionHealthOp: Operation<
  void,
  void,
  void,
  z.infer<typeof retentionHealthSchema>
> = {
  method: "GET",
  path: () => "/api/database/retention/health",
  response: retentionHealthSchema,
};

export const retention = {
  health: retentionHealthOp,
};
